import React, { createContext, useState, useContext, ReactNode, useCallback, useRef } from 'react';
import axios from 'axios';

interface AuthContextType {
  userInfo: OktaUser | null;
  setUserInfo: (authStatus: OktaUser) => void;
  appData: Record<string, any>;
  setAppData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  isLoadingAppData: boolean;
  setIsLoadingAppData: (loading: boolean) => void;
  dashboardData: any;
  setDashboardData: (data: any) => void;
  fetchDashboardData: (forceRefresh?: boolean) => Promise<any>;
}

export interface OktaUser {
  sub: string,
  name: string,
  profile: string,
  locale: string,
  email: string,
  nickname: string,
  preferred_username: string,
  given_name: string,
  family_name: string,
  zoneinfo: string,
  email_verified: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<OktaUser | null>(null);
  const [appData, setAppData] = useState<Record<string, any>>({});
  const [isLoadingAppData, setIsLoadingAppData] = useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<any>(null);

  // Use a ref to store the in-flight promise for deduplication
  const fetchPromiseRef = useRef<Promise<any> | null>(null);

  const fetchDashboardData = useCallback(async (forceRefresh: boolean = false) => {
    // If not a force refresh and we already have an in-flight request, return it
    if (!forceRefresh && fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    const fetchAction = async () => {
      try {
        if (!dashboardData) setIsLoadingAppData(true);

        const url = forceRefresh ? '/api/appdata/dashboard?refresh=true' : '/api/appdata/dashboard';
        const response = await axios.get(url, { withCredentials: true });

        setDashboardData(response.data);
        if (response.data.allApps) {
          setAppData(response.data.allApps);
        }

        return response.data;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        throw error;
      } finally {
        setIsLoadingAppData(false);
        fetchPromiseRef.current = null;
      }
    };

    fetchPromiseRef.current = fetchAction();
    return fetchPromiseRef.current;
  }, [dashboardData]);

  const value = React.useMemo(() => ({
    userInfo,
    setUserInfo,
    appData,
    setAppData,
    isLoadingAppData,
    setIsLoadingAppData,
    dashboardData,
    setDashboardData,
    fetchDashboardData
  }), [userInfo, appData, isLoadingAppData, dashboardData, fetchDashboardData]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
